import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner, Badge } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import debounce from 'lodash/debounce';

import CommonSalesInternationalFields from "../../Sales/PumpSpareInternational/CommonSalesInternationalFields";
const CreatePumpInternationalSalesReturn = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [status, setStatus] = useState("Open");

    // Form state
    const [formData, setFormData] = useState({
        customer: null,
        customerDetails: {
            contactperson: '',
            contactno: '',
            email: '',
            paymentterm: '',
            incoterms: ''
        },
        customerMultipleAddress: null,
        shipTo: null,
        billTo: null,
        branch: null,
        warehouse: null,
        tallyreferenceno: '',
        dispatchthrough: '',
        destination: '',
        reference: '',
        packinglistno: '',
        packinglistdate: '',
        buyerorderno: '',
        buyerorderdate: '',
        rows: [],
        exchangeRate: 1,
        exportchargescurrency: 0,
        exportpreperationcharges: '',
        selectedCurrency: " "

    });

    const [invoiceno, setInvoiceno] = useState('');
    const [invoicedate, setInvoicedate] = useState('');

    const [salesreturnno, setsalesreturnno] = useState('');
    const [salesreturndate, setsalesreturndate] = useState('');
    const [enquiryValidationMsg, setEnquiryValidationMsg] = useState('');
    const [isEnquiryValid, setIsEnquiryValid] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);


    const checksalesreturnno = async (number) => {
        if (!number) {
            setEnquiryValidationMsg('');
            setIsEnquiryValid(false);
            return;
        }


        if (!/^[A-Za-z0-9-]+$/.test(number)) {
            setEnquiryValidationMsg('❌ Only alphanumeric characters and hyphens are allowed');
            setIsEnquiryValid(false);
            return;
        }

        try {
            const response = await axios.get(`${BASE_URL}salesReturn/check/${number}`, {
                headers: authHeader(),
            });

            if (response.data === true) {
                setEnquiryValidationMsg('❌ This sales return number already exists.');
                setIsEnquiryValid(false);
            } else if (response.data === false) {
                setEnquiryValidationMsg('✅ This sales return number is available.');
                setIsEnquiryValid(true);
            } else {
                setEnquiryValidationMsg('Unexpected response from server.');
                setIsEnquiryValid(false);
            }
        } catch (error) {
            console.error("Error checking sales return number:", error);
            setEnquiryValidationMsg('❌ Error validating sales return number.');
            setIsEnquiryValid(false);
        }
    };


    useEffect(() => {
        const debouncedCheck = debounce(() => {
            checksalesreturnno(salesreturnno);
        }, 400);

        debouncedCheck();

        return () => debouncedCheck.cancel();
    }, [salesreturnno]);


    const handleDataChange = (newData) => {
        setFormData(prev => ({
            ...prev,
            ...newData,
            rows: newData.rows || prev.rows
        }));
    };


    const handleSubmit = async () => {
        setFormSubmitted(true);
        if (!isEnquiryValid) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please enter a valid sales number before submitting.',
            });
            return;
        }

        const {
            customer,
            customerDetails,
            customerMultipleAddress,
            shipTo,
            billTo,
            branch,
            warehouse,
            tallyreferenceno,
            dispatchthrough,
            destination,
            reference,
            packinglistno,
            packinglistdate,
            buyerorderno,
            buyerorderdate,
            rows,
            exchangeRate,
            exportchargescurrency,
            exportpreperationcharges,
            selectedCurrency
        } = formData;

        // Validation
        if (!customer) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please select a customer',
            });
            setIsLoading(false);
            return;
        }

        if (rows.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please add at least one product',
            });
            setIsLoading(false);
            return;
        }

        setIsLoading(true);


        // Calculate totals
        const misccharges = exportchargescurrency * exchangeRate;
        const netTotalInr = rows.reduce((total, row) => total + (row.totalInr || 0), 0);
        const netTotalCurrency = rows.reduce((total, row) => total + (row.totalcurrency || 0), 0);
        const grandTotalInr = netTotalInr + misccharges;
        const grandTotalCurrency = netTotalCurrency + exportchargescurrency;

        // Prepare sales items
        const salesReturnItems = rows.map(row => ({
            product: { id: row.value },
            productremarks: row.productremarks || '',
            quantity: row.quantity || 0,
            unitrateinr: row.unitRateInr || 0,
            totalinr: row.totalInr || 0,
            unitratecurrency: row.unitratecurrency || 0,
            totalcurrency: row.totalcurrency || 0,
            exchangerate: row.exchangerate || exchangeRate,
            itemcode: row.itemcode || '',
            hsnsacno: row.hsnsacno || '',
            serialnumber: row.serialnumber || '',
            modelnumber: row.modelnumber || '',
            tagnumber: row.tagnumber || '',
            moc: row.moc || '',
            partno: row.partno || '',
            uom: row.uom || '',
            currency: selectedCurrency,
        }));

        // Prepare main Sales data
        const SalesData = {
            salesreturnno,
            salesreturndate,
            status: status,
            salesreturntype: "Pump Spare",
            salesreturncategory: "International",
            tallyreferenceno,
            dispatchthrough,
            destination,
            reference,
            packinglistno,
            packinglistdate,
            buyerorderno,
            buyerorderdate,
            invoiceno,
            invoicedate,
            salesReturnItems,
            customer: { id: customer.value },
            customeraddress: customerMultipleAddress ? { id: customerMultipleAddress.value } : null,
            shipto: shipTo ? { id: shipTo.value } : null,
            billto: billTo ? { id: billTo.value } : null,
            contactperson: customerDetails.contactperson,
            contactno: customerDetails.contactno,
            email: customerDetails.email,
            paymentterm: customerDetails.paymentterm,
            incoterms: customerDetails.incoterms,
            branch: branch ? { id: branch.value } : null,
            warehouse: warehouse ? { id: warehouse.value } : null,
            salescurrency: selectedCurrency,
            salesexchangerate: exchangeRate,
            grandtotalinr: grandTotalInr,
            grandtotalcurrency: grandTotalCurrency,
            exportpreperationchargescurrency: exportchargescurrency,
            exportpreperationcharges,
            nettotalinr: netTotalInr,
            nettotalcurrency: netTotalCurrency,
        };

        try {
            await axios.post(`${BASE_URL}salesReturn/save`, SalesData, {
                headers: authHeader()
            });

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: ' successfully!',
            });
            navigate(`/srindex/1`);
        } catch (error) {
            console.error("Error updating Sales:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to create sales return. Please try again.',
            });
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <div className="container">
            <br />
            <Form>
                <Row>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Sales Return  No.: *</Form.Label>
                            <Form.Control
                                type="text"
                                value={salesreturnno}
                                onChange={e => {
                                    setsalesreturnno(e.target.value);
                                    setFormSubmitted(false);
                                }}
                                isInvalid={
                                    (formSubmitted && !isEnquiryValid) ||
                                    enquiryValidationMsg.includes('exists') ||
                                    enquiryValidationMsg.includes('Error') ||
                                    enquiryValidationMsg.includes('allowed')
                                }
                                required
                            />
                            {enquiryValidationMsg && (
                                <Form.Control.Feedback type={
                                    enquiryValidationMsg.includes('exists') ||
                                        enquiryValidationMsg.includes('Error') ||
                                        enquiryValidationMsg.includes('allowed') ?
                                        "invalid" : "valid"
                                }>
                                    {enquiryValidationMsg}
                                </Form.Control.Feedback>
                            )}
                            {formSubmitted && !isEnquiryValid && !enquiryValidationMsg && (
                                <Form.Control.Feedback type="invalid">
                                    Please enter a valid enquiry number
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Sales Return Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={salesreturndate}
                                onChange={(e) => setsalesreturndate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Sales Invoice No.</Form.Label>
                            <Form.Control
                                type="text"
                                value={invoiceno}
                                onChange={e => setInvoiceno(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Sales Invoice Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={invoicedate}
                                onChange={(e) => setInvoicedate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <CommonSalesInternationalFields
                    onDataChange={handleDataChange}
                    initialData={formData}
                    isUpdate={true}
                />

                <br />
                <div className="d-flex justify-content-end">
                    <button
                        className="btn btn-primary me-2"
                        type="button"
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" />
                                Updating...
                            </>
                        ) : 'Create Sales Return'}
                    </button>

                    <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => navigate(`/salesView/${id}`)}
                        disabled={isLoading}
                    >
                        CANCEL
                    </button>
                </div>
            </Form>
        </div>
    );
};

export default CreatePumpInternationalSalesReturn;
